<template>
    <v-container>
        <v-container>
            <v-row>
                <v-col cols="12" sm="12" md="6" v-for="(veiculo, v) in cardsFiltered" :key="veiculo.id_linha + '-' + veiculo.sentido">
                    <v-card rounded :loading="loading" :color="getCardColor(veiculo) + '80'" :class="getCardClass(v)">
                        <v-card-title>
                            <v-row>
                                <v-col cols="12" sm="8" align-self="center">
                                    <div class="card__title">
                                        {{ veiculo.nome_linha }} - {{ getSentido(veiculo.sentido) }}
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="2" align-self="center">
                                    <v-row justify="center">
                                        <v-icon class="responsive-icon"
                                            style="margin-left: 10px; padding-top: 5px;">mdi-bus</v-icon>
                                    </v-row>
                                    <v-row justify="center">
                                        <span
                                            style="margin-left: 10px; font-size: 0.7em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;">{{
                                                veiculo.cod_veiculo }}</span>
                                    </v-row>
                                </v-col>
                                <v-col cols="12" sm="2" align-self="center">
                                    <v-row justify="center">
                                        <div class="status__linha" v-if="veiculo.linha_atrasada == 1">{{ $t('painel.late') }}
                                        </div>
                                    </v-row>
                                    <v-row justify="center">
                                        <v-icon color="black">{{ veiculo.em_viagem == 1 ? 'mdi-bus-clock' : 'mdi-clock' }}</v-icon>
                                        <div class="eta">{{ getTempoChegada(veiculo) }}</div>
                                    </v-row>
                                    <v-row justify="center" v-if="veiculo.em_viagem != 1">
                                        <div class="eta__unavailable">{{ $t('painel.scheduled') }}</div>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </v-card-title>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

    </v-container>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";

const NUMERO_CARDS_PADRAO = 10;
const INTERVALO_ATUALIZACAO_MS = 60000;
const INTERVALO_RELOGIO_MS = 15000;

/**
 * Painel de parada. O aparelho do painel não tem GPS, então o ponto vem pela URL:
 *   /painel?cerca=<código da cerca>&cards=<máximo de cards>
 * (`parada` é aceito como sinônimo de `cerca`). Sem o parâmetro, cai na geolocalização do navegador.
 *
 * Um card por linha+sentido que passa no ponto: se há ônibus em viagem que ainda não passou pelo
 * ponto, mostra o tempo estimado de chegada dele; senão, o horário programado da próxima viagem.
 */
export default {
    data: () => ({
        loading: false,
        veiculos: [],
        parada: null,
        timeout: null,
        relogio: null,
        agora: Date.now(),
        lat: null,
        lng: null,
        windowWidth: window.innerWidth, // Para forçar reatividade
    }),
    computed: {
        codCercaUrl() {
            const q = this.$route.query;
            const valor = q.cerca ?? q.parada;
            return valor != null && String(valor).trim() !== '' ? String(valor).trim() : null;
        },
        numeroDeCards() {
            const n = parseInt(this.$route.query.cards, 10);
            return Number.isFinite(n) && n > 0 ? n : NUMERO_CARDS_PADRAO;
        },
        cards() {
            // Próxima chegada por linha+sentido: ônibus em viagem tem prioridade sobre a programação.
            const porLinha = new Map();
            for (const v of this.veiculos || []) {
                const chave = `${v.id_linha}-${v.sentido}`;
                const atual = porLinha.get(chave);
                if (!atual || this.comparaCards(v, atual) < 0) {
                    porLinha.set(chave, v);
                }
            }
            // Ônibus a caminho primeiro (pela chegada estimada), depois as programadas (pelo horário):
            // programada atrasada ainda nem saiu da origem, não chega antes de quem já está a caminho.
            return Array.from(porLinha.values()).sort(this.comparaCards);
        },
        cardsFiltered() {
            return this.cards.slice(0, this.numeroDeCards);
        },
    },
    watch: {
        codCercaUrl() {
            this.initialize();
        },
    },
    created() {
        this.initialize();
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
        this.relogio = setInterval(() => { this.agora = Date.now(); }, INTERVALO_RELOGIO_MS);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        clearInterval(this.relogio);
        clearTimeout(this.timeout);
        // Limpar a localização quando sair da tela
        this.$root.$emit('setLocalizacao', null);
    },
    methods: {
        initialize() {
            clearTimeout(this.timeout);
            this.parada = null;
            this.veiculos = [];
            this.loading = true;

            if (this.codCercaUrl) {
                this.populaParada({ cod_cerca: this.codCercaUrl });
                return;
            }

            if (!navigator.geolocation) {
                this.loading = false;
                EventBus.$emit('show-alert', { mensagem: this.$t('painel.errors.locationError'), tipo: 'error' });
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    this.lat = pos.coords.latitude;
                    this.lng = pos.coords.longitude;
                    this.populaParada({ coords: { center: { lat: this.lat, lng: this.lng } } });
                },
                () => {
                    this.loading = false;
                    EventBus.$emit('show-alert', { mensagem: this.$t('painel.errors.locationError'), tipo: 'error' });
                }
            );
        },
        async populaParada(body) {
            try {
                const response = await apiController.request(apiController.endpoints.getCercamaisproxima, {
                    data: body,
                    silent: true,
                });
                const d = response.data;
                const row = Array.isArray(d) ? (d[0] || null) : null;
                if (!row) {
                    this.loading = false;
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t(this.codCercaUrl ? 'painel.errors.stopNotFound' : 'painel.errors.stopError'),
                        tipo: 'error'
                    });
                    return;
                }
                this.parada = row;
                this.$root.$emit('setLocalizacao', this.parada.nome_cerca);
                this.populaPainel();
            } catch (error) {
                this.loading = false;
                EventBus.$emit('show-error', { error });
            }
        },
        async populaPainel() {
            if (!this.parada) return;
            this.loading = true;
            clearTimeout(this.timeout);

            try {
                const response = await apiController.request(apiController.endpoints.getVeiculospainel, {
                    data: { id_cerca: this.parada.id_cerca },
                    silent: true,
                });
                this.veiculos = Array.isArray(response.data) ? response.data : [];
                this.agora = Date.now();
            } catch (error) {
                EventBus.$emit('show-error', { error });
            } finally {
                this.loading = false;
                // Painel fica ligado direto: segue atualizando mesmo depois de uma falha.
                this.timeout = setTimeout(() => this.populaPainel(), INTERVALO_ATUALIZACAO_MS);
            }
        },
        /** Ônibus em viagem está parado no ponto do painel. */
        noPonto(veiculo) {
            return veiculo.em_viagem == 1 && this.parada && veiculo.id_cerca == this.parada.id_cerca;
        },
        /** Segundos até o ônibus em viagem chegar ao ponto (ETA descontado o tempo desde o cálculo), ou null. */
        etaSegundos(veiculo) {
            if (veiculo.em_viagem != 1) return null;
            if (this.noPonto(veiculo)) return 0;
            if (veiculo.has_eta != 1 || veiculo.eta_segundos_proxima_parada == null) return null;
            const eta = Number(veiculo.eta_segundos_proxima_parada);
            if (!Number.isFinite(eta)) return null;
            const calculadoEm = veiculo.dt_atualizacao_eta ? new Date(veiculo.dt_atualizacao_eta).getTime() : NaN;
            const decorrido = Number.isFinite(calculadoEm) ? Math.max(0, (this.agora - calculadoEm) / 1000) : 0;
            return Math.max(0, eta - decorrido);
        },
        /** Instante (ms) usado para ordenar: chegada estimada do ônibus ou saída programada. */
        instanteChegada(veiculo) {
            const eta = this.etaSegundos(veiculo);
            if (eta != null) return this.agora + eta * 1000;
            const saida = veiculo.dt_saida_viagem ? new Date(veiculo.dt_saida_viagem).getTime() : NaN;
            return Number.isFinite(saida) ? saida : Number.MAX_SAFE_INTEGER;
        },
        comparaCards(a, b) {
            const aViagem = this.etaSegundos(a) != null;
            const bViagem = this.etaSegundos(b) != null;
            if (aViagem !== bViagem) return aViagem ? -1 : 1;
            return this.instanteChegada(a) - this.instanteChegada(b);
        },
        getCardColor(veiculo) {
            if (veiculo.linha_atrasada == 1) {
                return "#c20006";
            }

            return "#02b31f";
        },
        getCardClass(index) {
            if (index === 0) {
                return "next__trip";
            }

            return null;
        },
        handleResize() {
            this.windowWidth = window.innerWidth;
        },
        getTempoChegada(veiculo) {
            const eta = this.etaSegundos(veiculo);
            if (eta != null) {
                const minutos = Math.floor(eta / 60);
                return minutos < 1 ? this.$t('painel.now') : this.$t('painel.minutes', { n: minutos });
            }

            if (veiculo.horario_saida_viagem) {
                return veiculo.horario_saida_viagem;
            }

            return "-";
        },
        getSentido(sentido) {
            if (sentido == 1) {
                return this.$t('painel.direction.outbound');
            } else if (sentido == 2) {
                return this.$t('painel.direction.inbound');
            }

            return "";
        },
    },
}
</script>

<style scoped>
.card__title {
    white-space: nowrap;
    word-break: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    font-size: 0.9em;
}

.status__linha {
    color: #b10107;
    font-weight: bold;
    font-size: 0.85em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.eta {
    font-weight: bold;
    font-size: 0.9em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.next__trip {
    /* Efeito de alternância de cor entre mais claro e mais escuro */
    animation: colorPulse 0.5s infinite alternate;
    border: solid black 2px;
}

@keyframes colorPulse {
    /* A cor mais clara será a cor do card normal (herdada via background-color), a mais escura será uma versão escurecida */
    0% {
        /* Mantém a cor normal do card (herdada) */
        filter: brightness(1);
    }
    100% {
        /* Escurece a cor do card */
        filter: brightness(0.7);
    }
}

/* Garantir que o conteúdo não vaze */
.v-card__title {
    overflow: hidden;
}

.v-row {
    margin: 0;
}

.v-col {
    overflow: hidden;
}

/* Cards responsivos em altura */
.v-card {
    min-height: auto !important;
}

/* Padding responsivo dos cards */
@media (min-width: 599px) {
    .v-card {
        max-height: 120px !important;
        /* Mobile: cards mais altos */
    }

    .v-card__title {
        padding: 12px !important;
        /* Mobile: mais padding */
    }
}

@media (min-width: 600px) {
    .v-card {
        max-height: 120px !important;
        /* Small: altura média */
    }

    .v-card__title {
        padding: 10px !important;
        /* Small: padding médio */
    }
}

/* Ícone responsivo */
.responsive-icon {
    font-size: 1.5rem !important;
    /* Tamanho padrão para mobile */
}

/* Breakpoints responsivos para o ícone */
@media (min-width: 600px) {
    .responsive-icon {
        font-size: 1.8rem !important;
        /* sm */
    }
}

.eta__available {
    color: #000000;
    font-weight: bold;
    font-size: small;
}

.eta__unavailable {
    color: #4a4a4a;
    font-size: small;
}
</style>